// ---------------------------------------------------------------------------
// Experiment Configs — type-safe experiment definitions
// ---------------------------------------------------------------------------
//
// When adding a new experiment, define its config here. The `variations` tuple
// tells TypeScript exactly which variation strings are valid. The `useExperiment`
// hook uses this to narrow the returned `variation` field so you get
// autocomplete and compile-time errors if you check for a variation that
// doesn't exist.
//
// Common variation patterns:
//   - Standard A/B:    ['control', 'on'] DEFAULT
//   - Multi-variant:   ['control', 'variant_1', 'variant_2']
//
// Defining a config here is NOT enough to make an experiment work. The server
// only decides the keys listed in `OPTIMIZELY_EXPERIMENTS` in
// `constants/features/*`, so an unregistered experiment never reaches the
// client and `useExperiment` reports it as turned off in every environment,
// however it is configured in Optimizely. Register the key there as well.
// ---------------------------------------------------------------------------

/**
 * A type-safe experiment configuration that links an experiment ID
 * to its known variation strings.
 */
export interface ExperimentConfig<
  TId extends string = string,
  TVars extends ReadonlyArray<string> = ReadonlyArray<string>,
> {
  /** The provider experiment ID. */
  readonly id: TId
  /** The exhaustive list of variation strings for this experiment. */
  readonly variations: TVars
  /** The experiment provider. Defaults to Optimizely for existing experiments. */
  readonly provider?: 'optimizely' | 'cherto'
}

export interface ChertoExperimentConfig<
  TId extends string = string,
  TVars extends ReadonlyArray<string> = ReadonlyArray<string>,
> extends ExperimentConfig<TId, TVars> {
  readonly provider: 'cherto'
  readonly keyStrategy: 'environment-qualified' | 'production-only'
  /**
   * Cherto unit this experiment randomizes on.
   * `user-id` is Customer BK. Login refreshes Cherto for those experiments
   * even when the anonymous bootstrap had no assignment.
   * `domain-userid` keeps the existing seed id.
   */
  readonly unitType: 'domain-userid' | 'user-id'
}

/** Default variations for standard A/B experiments. */
const DEFAULT_VARIATIONS = ['control', 'on'] as const

/** Helper to create an ExperimentConfig with full type inference. */
function defineExperiment<
  TId extends string,
  const TVars extends ReadonlyArray<string>,
>(id: TId, variations: TVars, provider?: ExperimentConfig['provider']): ExperimentConfig<TId, TVars>
function defineExperiment<TId extends string>(id: TId, provider?: ExperimentConfig['provider']): ExperimentConfig<TId, typeof DEFAULT_VARIATIONS>
function defineExperiment<
  TId extends string,
  const TVars extends ReadonlyArray<string>,
>(id: TId, variationsOrProvider?: TVars | ExperimentConfig['provider'], provider?: ExperimentConfig['provider']) {
  const variations = Array.isArray(variationsOrProvider) ? variationsOrProvider : DEFAULT_VARIATIONS
  const experimentProvider = Array.isArray(variationsOrProvider) ? provider : variationsOrProvider

  return {
    id,
    variations,
    ...(experimentProvider ? { provider: experimentProvider } : {}),
  }
}

function defineChertoExperiment<
  TId extends string,
  const TVars extends ReadonlyArray<string>,
>(
  id: TId,
  variations: TVars,
  unitType: ChertoExperimentConfig['unitType'] = 'domain-userid',
): ChertoExperimentConfig<TId, TVars> {
  if (id.startsWith('prod-') || id.startsWith('staging-')) {
    throw new Error(`Cherto experiment "${id}" must use its logical key without an environment prefix`)
  }

  return {
    ...defineExperiment(id, variations, 'cherto'),
    provider: 'cherto',
    keyStrategy: 'environment-qualified',
    unitType,
  }
}

function defineProductionOnlyChertoExperiment<
  TId extends string,
  const TVars extends ReadonlyArray<string>,
>(
  id: TId,
  variations: TVars,
  unitType: ChertoExperimentConfig['unitType'] = 'domain-userid',
): ChertoExperimentConfig<TId, TVars> {
  return {
    ...defineExperiment(id, variations, 'cherto'),
    provider: 'cherto',
    keyStrategy: 'production-only',
    unitType,
  }
}

/**
 * Type-safe experiment configs.
 *
 * Pass these to `useExperiment()` instead of raw experiment IDs to get typed
 * `variation` in the result — preventing typos and making
 * exhaustive checks possible.
 *
 * Variations default to `['control', 'on']`. Only specify variations when the
 * experiment uses non-standard ones (e.g. multi-variant).
 *
 * Usage:
 * ```ts
 * // Standard A/B — variations default to ['control', 'on']
 * const { isAnyVariant } = useExperiment(Experiments.myExperiment)
 *
 * // Multi-variant — specify variations explicitly
 * const { variation } = useExperiment(Experiments.myMultiVariant)
 * // variation: 'control' | 'variant_a' | 'variant_b' | undefined
 * ```
 */
export const Experiments = {
  searchExperiencesNls: defineExperiment('search_experiences_nls'),
  isSupportAssistantEnabled: defineExperiment('opex_support_assistant_customer_pool'),
  emailChangeEnabled: defineExperiment('opex_email_change'),
  securePhoneNumberChangeEnabled: defineExperiment('csx_secure_phone_number_change'),
  croAlwaysOnNewMobileBooking: defineExperiment('cro_lpc_new_mobile_booking'),
  paymentsDepositOptionForHotelsAbove1999V2: defineExperiment('payments_deposit_option_for_hotels_above_1999_v2'),
  carHireBundledDiscountUpcomingOrderHomepageCarousel: defineExperiment('car_hire_bundled_discount_upcoming_order_homepage_carousel'),
  croPopularOptionBookingRemaining: defineExperiment('cro_popular_option_booking_remaining'),
  growthShowOfferUrgencyCountdownLabel: defineExperiment('growth_force_show_offer_urgency_countdown_label'),
  croBundledFlexibleFlightFaresV2: defineExperiment('cro_bundled_flexible_flight_fares_v2'),
  croHideHomepageSearchEmergingMarkets: defineExperiment('cro_hide_homepage_search_emerging_markets'),
  lerePersonalisedHomePageHeroCarousel: defineExperiment('lere_personalised_home_page_hero_carousel'),
  lerePersonalisedHomePageHeroCarouselV7: defineExperiment('lere_home_page_carousel_v7'),
  croPrefetchInflowCheckoutExperiences: defineExperiment('cro_prefetch_inflow_checkout_experiences'),
  flightsPrefetchFlightsFromOffer: defineExperiment('flights_prefetch_flights_from_offer'),
  flightsCombinedReturnLegs: defineExperiment('flights_combined_return_legs'),
  flightsBundledCombinedReturnLegs: defineExperiment('flights_bundled_combined_return_legs'),
  flightsInlineUpsell: defineExperiment('flights_inline_upsell'),
  desktopSteppedCheckout: defineExperiment('desktop_stepped_checkout', ['off', 'on'] as const),
  carHireObjectiveIntentMessage: defineExperiment('car_hire_objective_intent_message'),
  croFlexCfmrBedbankPackages: defineExperiment('cro_flex_cfmr_bedbank_packages'),
  growthMetasearchPerNightPricing: defineExperiment('growth_metasearch_per_night_pricing'),
  growthMetasearchMobileDesignV2: defineExperiment('growth_metasearch_mobile_design_v2'),
  growthMetasearchGuestCheckout: defineExperiment('growth_metasearch_guest_checkout'),
  /**
   * Tiered LPP promo for a fixed set of Google CPC metasearch campaigns. Bucketing and
   * activation happen on the LPP offer page reached from the metasearch first tile — see
   * `useMetasearchLppPromoExperiment`.
   */
  growthMetasearchLppPromo: defineExperiment('growth_metasearch_lpp_promo'),
  croOrderDownloadPdf: defineExperiment('cro_order_download_pdf'),
  cruiseAutoFocusNextFilter: defineExperiment('cruise_auto_focus_next_filter'),
  cruiseMpvRanking: defineExperiment('cruise_mpv_ranking'),
  labnableVideoBackground: defineExperiment('lab_enable_video_background'),
  loyaltyCommSubBonus: defineExperiment(
    'loyalty_communication_resubscription_bonus',
    ['control', '500_points', '750_points'] as const,
  ),
  growthPaidOfferGalleryVariant: defineExperiment('growth_paid_offer_gallery_variant'),
  croExpressCheckoutAutoDefault: defineExperiment('cro_express_checkout_auto_default'),
  croLoginWithFacebook: defineExperiment('cro_login_with_facebook'),
  seoStaticHotelsV2Ab: defineExperiment('seo_static_hotels_v2_ab'),
  luxPlusReconsiderCheckoutBanner: defineExperiment('lux_plus_reconsider_checkout_banner'),
  croPostTripReviewHomepage: defineExperiment('cro_post_trip_review_homepage_v2'),
  opexLESAEmptyChatFreeText: defineExperiment('opex_lesa_empty_chat_free_text_ab_test'),
  opexBookingAuthorityReferralBannerAbTest: defineExperiment(
    'opex_booking_authority_referral_banner_ab_test_v2',
    ['control', 'variant_with_referral_banner', 'referral_banner_variant_no_t_and_c'] as const,
  ),
  // Funnel optimiser A/B tests (see src/checkout/selectors/funnel.ts for usage)
  funnelOptimiserRuleId16: defineExperiment('funnel_optimiser_ruleid_16'),
  funnelOptimiserRuleId17: defineExperiment('funnel_optimiser_ruleid_17'),
  funnelOptimiserRuleId18: defineExperiment('funnel_optimiser_ruleid_18'),
  paymentsLpc50PercentDepositSchedule: defineExperiment('payments_lpc_50_percent_deposit_schedule'),
  lereSimilarToCarouselV2: defineExperiment('lere_similar_to_carousel_v2', ['control', 'on'] as const),
  opexLesaNlsOffersCarousel: defineExperiment('opex_lesa_nls_offers_carousel_ab_test'),
  cruiseOfferPageEarlyTravellerSelection: defineExperiment('cruise_offer_page_early_traveller_selection'),
  cruiseCheckoutTravellerDetailsPlacement: defineExperiment(
    'cruise_checkout_traveller_details_placement',
    ['control', 'variant'] as const,
  ),
  opexCheckoutOptionalGuestTravellerForms: defineExperiment('opex_checkout_optional_guest_traveller_forms'),
  opexCheckoutTravellerCardShareBooking: defineExperiment('opex_checkout_traveller_card_share_booking'),
  croCheckoutCancellationPolicyTimeline: defineExperiment('cro_checkout_cancellation_policy_timeline_2'),
  internationalCheckoutFlow: defineExperiment('international_checkout_flow'),
  croMobileWebAppLikeCheckout: defineExperiment('cro_mobile_web_app_like_checkout'),
  paymentsLpp50PercentDepositSchedule: defineExperiment('payments_lpp_50_percent_deposit_schedule'),
  /**
   * Controls visibility of the points burn UI on search results pages (toggle + slider).
   * This does NOT affect points burn at checkout, which has its own separate enablement logic.
   */
  loyaltySocieteBurn: defineExperiment('loyalty_societe_burn'),
  searchSalesCtrPaidTrafficRanking: defineExperiment('search_sales_ctr_paid_traffic_ranking'),
  searchLowViewDestinationsSalesCtrRanking: defineExperiment('search_low_view_destinations_sales_ctr_ranking'),
  searchNearCentreFlashBoostRanking: defineExperiment('search_near_centre_flash_boost'),
  searchDatedPriceCacheSort: defineExperiment('search_dated_price_cache_sort'),
  searchPropertyNearbyDensityRanking: defineExperiment('search_property_nearby_density_ranking'),
  searchLowViewConfidenceRanking: defineExperiment('search_low_view_confidence_ranking'),
  searchSponsoredAds: defineExperiment('search_sponsored_ads'),
  searchMapRedesign: defineExperiment('search_map_redesign'),
  searchInternationalRanking: defineExperiment('search_international_ranking'),
  croOfferPageHighlightTagsV2: defineExperiment('cro_offer_page_highlight_tags_v2'),
  ghaSppOnlyShowInterestedRate: defineExperiment('gha-spp-only-show-interested-rate', ['control', 'on'] as const),
  ghaPriceMatch: defineExperiment('growth_metasearch_sourcing_selling', ['off', 'on'] as const),
  croRoomAmenitiesInsteadOfDescription: defineExperiment('cro_room_amenities_instead_of_description'),
  croUrgencyOnSppRooms: defineExperiment('cro_urgency_on_spp_rooms'),
  nhaHomesAndVillasToggleInSearch: defineExperiment('nha_homes_and_villas_toggle_in_search'),
  searchTilePropertyNearby: defineExperiment('search_tile_property_nearby'),
  commsCompactMobileSearchTile: defineExperiment('comms_compact_mobile_search_tile'),
  tripChatMobile: defineExperiment('trip_chat_mobile'),
  croPcmTestimonialOnOfferPage: defineExperiment('cro_pcm_testimonial_on_offer_page'),
  croSoldOutFlashPackagesState: defineExperiment('cro_sold_out_flash_packages_state'),
  croFixedFlashLengthOfStay: defineExperiment('cro_fixed_flash_length_of_stay'),
  croSocialProofSpp: defineExperiment('cro_social_proof_spp'),
  croSocialProofTours: defineExperiment('cro_social_proof_tours'),
  croOneClickBookingRestoration: defineExperiment('cro_one_click_booking_restoration'),
  croFlashPackageTickbox: defineExperiment('cro_flash_package_tickbox'),
  standaloneFlightUpsellExperiences: defineExperiment('standalone_flight_upsell_experiences'),
  tripHotelOfferSurface: defineExperiment('trip-hotel-offer-surface'),
  searchHiddenPreviewBannerSearchResult: defineExperiment('search_hidden_preview_banner_search_result'),
  searchCalendarEventOverlay: defineExperiment('search_calendar_event_overlay'),
  sponsoredSearchTile: defineExperiment('sponsored_search_tile'),
  croHomepageHeroOffersSsrTimeout: defineExperiment('cro_homepage_hero_offers_ssr_timeout'),
} as const

export const ChertoExperiments = {
  searchRecommendationsReranking: defineChertoExperiment('lere-search-rerank', ['control', 'variant'] as const),
  croSppPackageSelection: defineChertoExperiment('cro-spp-package-selection-v2', ['control', 'option-d'] as const),
  // Existing live rollout uses this literal key without environment prefixes.
  flightsToursUpsell: defineProductionOnlyChertoExperiment('flights-tours-upsell', ['control', 'variant'] as const),
  croPreviouslyViewedOffers: defineChertoExperiment('cro-previously-viewed-offers', ['control', 'variant'] as const, 'user-id'),
  croHomepageSocieteLuxPlusCards: defineChertoExperiment(
    'cro-home-page-societe-luxplus-below-trending-destinations',
    ['control', 'variant'] as const,
  ),
  // CRO - Home page offer tiles
  croHomePageOfferTiles: defineChertoExperiment(
    'cro-home-page-offer-tiles',
    ['control', 'variant'] as const,
  ),
  carsAirportTransfer: defineChertoExperiment('transferz-experiences', ['control', 'variant'] as const),
  croAddPhoneGoogle: defineChertoExperiment('cro-add-phone-google', ['control', 'variant'] as const),
  croOfferTilePriceBlock: defineChertoExperiment(
    'cro-offer-tile-price-block',
    ['control', 'variant-a', 'variant-b'] as const,
  ),
  croLuxPlusInflowUpsell: defineChertoExperiment(
    'cro-luxplus-checkout-upsell',
    ['control', 'variant-a', 'variant-b'] as const,
  ),
  croMobileWebRoomUpgradeModal: defineProductionOnlyChertoExperiment(
    'cro-mobile-web-room-upgrade-modal-v5',
    ['control', 'on'] as const,
    'user-id',
  ),
  // Existing live experiments must retain their literal Cherto keys.
  croHideNearbyGooglePlaces: defineProductionOnlyChertoExperiment(
    'cro-hide-nearby-google-places-on-offer-page',
    ['control', 'variant'] as const,
  ),
  // Live Cherto key is prod-prefixed only — do not invent a staging- twin.
  croInsurancePriceIncreaseV2: defineProductionOnlyChertoExperiment(
    'prod-cro-insurance-price-increase-v2',
    ['control', 'variant-plus-5', 'variant-minus-5'] as const,
  ),
  croNewInsuranceUi: defineChertoExperiment('cro-new-insurance-ui-v2', ['control', 'variant'] as const),
  croPassportCardCruiseCoverDeselection: defineChertoExperiment(
    'cro-passportcard-cruise-cover-deselection',
    ['control', 'variant'] as const,
  ),
  croOfferPageReviewsAboveLocation: defineChertoExperiment(
    'cro-offer-page-reviews-above-location',
    ['control', 'variant'] as const,
  ),
  croCheckoutValidationGuidance: defineProductionOnlyChertoExperiment(
    'cro-checkout-validation-guidance',
    ['control', 'variant'] as const,
  ),
  croSocialProofRecentPurchase: defineProductionOnlyChertoExperiment('cro-social-proof-recent-purchase-v4', ['control', 'variant'] as const),
  croFacilitiesReworkV2: defineProductionOnlyChertoExperiment('cro-facilities-rework-v2', ['control', 'variant'] as const),
  croRemoveCheckAvailability: defineProductionOnlyChertoExperiment(
    'cro-remove-check-availability',
    ['control', 'variant'] as const,
  ),
  croMobileCompactSearchTile: defineProductionOnlyChertoExperiment(
    'cro-mobile-compact-search-tile',
    ['control', 'variant'] as const,
  ),
  homesCrossSellBannerVisibility: defineChertoExperiment(
    'hc-homes-cross-sell-banner-visibility',
    ['control', 'on'] as const,
  ),
  homesCrossSellBannerTrigger: defineChertoExperiment(
    'hc-homes-cross-sell-banner-trigger',
    ['control', 'variant'] as const,
  ),
  croCheckoutItemComponents: defineChertoExperiment(
    'cro-checkout-item-components',
    ['control', 'variant'] as const,
  ),
  homesAndVillasVacationRentalScope: defineChertoExperiment(
    'hc-homes-and-villas-vacation-rental-scope',
    ['control', 'variant'] as const,
  ),
  croFlashOfferLosCalendar: defineChertoExperiment(
    'cro-flash-offer-los-calendar',
    ['control', 'variant'] as const,
  ),
  croRedDotOnLoggedOutUserProfile: defineChertoExperiment(
    'cro-red-dot-on-logged-out-user-profile',
    ['control', 'variant'] as const,
  ),
  // LuxPlus+ Free Preview auto-enrolment, GB only. Cherto buckets it by User ID (member id), so a
  // customer keeps their arm on every visit and device and a control customer is never enrolled.
  // Registered on the user-id unit so a login refreshes its decision without a page load.
  // Activates once an eligible customer is logged in. See useLuxPlusFreePreviewAutoEnrolExistingUsers.
  luxPlusFreePreviewAutoEnrol: defineChertoExperiment(
    'growth-luxplus-free-preview-auto-enrol',
    ['control', 'variant'] as const,
    'user-id',
  ),
  croPaidSearchDesktopQuickSelect: defineChertoExperiment(
    'cro-paid-search-desktop-quick-select',
    ['control', 'variant'] as const,
  ),
  croHomepageFamilyFriendlyOfferCarousel: defineChertoExperiment(
    'cro-homepage-family-friendly-offer-carousel',
    ['control', 'variant'] as const,
  ),
  // Logged-in customers with a recent hotel destination search.
  // An upcoming hotel stay in that destination hides the carousel, including a
  // city inside a searched country or region. A different city, or a completed
  // stay, still shows it. User-id unit keeps the arm stable across devices.
  homePageRecentSearchesCarousel: defineChertoExperiment(
    'home-page-recent-searches-carousel',
    ['control', 'variant'] as const,
    'user-id',
  ),
  sppRecommendedRateInheritance: defineChertoExperiment(
    'spp-recommended-rate-inheritance-v2',
    ['control', 'lpc', 'any'] as const,
  ),
  // Homepage listing payloads. Domain user id includes logged-out traffic.
  croSummaryOfferPayload: defineChertoExperiment(
    'cro-summary-offer-payload',
    ['control', 'variant'] as const,
  ),
  // GR-5151 expiring voucher reminder. Cherto buckets it by User ID (member id), so a customer keeps
  // their arm on every visit and device. Registered on the user-id unit so a login refreshes its
  // decision without a page load. Activates once a logged-in customer has an expiring voucher.
  // See useExpiringVoucherReminderExperiment.
  expiringVoucherReminder: defineChertoExperiment(
    'voucher-expiry-experiment',
    ['control', 'variant'] as const,
    'user-id',
  ),
  // GR-5145 hide the promo code field from affiliate-referred (Impact) traffic. Bucketed by domain
  // user id because affiliate visitors often land logged out. Activates at checkout once an affiliate
  // customer's cart would otherwise accept a promo code. See useAffiliatePromoCodeExperiment.
  affiliateHidePromoCode: defineChertoExperiment(
    'affiliate-hide-promo-code',
    ['control', 'variant'] as const,
  ),
} as const satisfies Record<string, ChertoExperimentConfig<string>>

const chertoCustomerBkExperimentIds = new Set<string>(
  Object.values(ChertoExperiments)
    .filter(experiment => experiment.unitType === 'user-id')
    .map(experiment => experiment.id),
)

/** True when this experiment is registered on the customer unit. */
export function isChertoCustomerBkExperiment(experimentId: string) {
  return chertoCustomerBkExperimentIds.has(experimentId)
}

/** True when this brand runs a Cherto experiment registered on the customer unit. */
export function brandHasChertoCustomerBkExperiment(enabledExperimentIds: ReadonlyArray<string>) {
  return enabledExperimentIds.some(isChertoCustomerBkExperiment)
}

export type Experiment =
  | typeof Experiments[keyof typeof Experiments]
  | typeof ChertoExperiments[keyof typeof ChertoExperiments]

export type ExperimentId = Experiment['id']
